import { useCallback, useState } from 'react';
import type {
  IFilterContext,
  TStringArrayKeys,
} from '@/creditflowresearch/application/BondScreener/createUseMultiSelectFilter';

interface IUseAbsoluteFilterProps<T> {
  includedKey: TStringArrayKeys<T>,
  excludedKey: TStringArrayKeys<T>,
  allValues: string[],
  toggle: (value: string) => void,
  toggleAll: () => void,
  useFilterContext: () => IFilterContext<T>,
}

interface IUseAbsoluteFilterReturn {
  isAbsolute: boolean,
  onToggleAbsolute: () => void,
  toggle: (value: string) => void,
  toggleAll: () => void,
}

const useAbsoluteFilter = <T extends Record<string, unknown>>({
  includedKey,
  excludedKey,
  allValues,
  toggle,
  toggleAll,
  useFilterContext,
}: IUseAbsoluteFilterProps<T>): IUseAbsoluteFilterReturn => {
  const { localFilters, setLocalFilters } = useFilterContext();
  const [isAbsolute, setIsAbsolute] = useState(() => ((localFilters[excludedKey] as string[])?.length ?? 0) > 0);

  const getExcluded = useCallback(
    (included: string[]): string[] => allValues.filter((v) => !included.includes(v)),
    [allValues],
  );

  const syncExcluded = useCallback(() => {
    if (!isAbsolute) return;
    setLocalFilters((prev) => ({
      ...prev,
      [excludedKey]: getExcluded(prev[includedKey] as string[]),
    }));
  }, [isAbsolute, includedKey, excludedKey, setLocalFilters, getExcluded]);

  const onToggleAbsolute = useCallback(() => {
    setIsAbsolute((wasAbsolute) => {
      setLocalFilters((prev) => ({
        ...prev,
        [excludedKey]: wasAbsolute ? [] : getExcluded(prev[includedKey] as string[]),
      }));
      return !wasAbsolute;
    });
  }, [includedKey, excludedKey, setLocalFilters, getExcluded]);

  const decoratedToggle = useCallback((value: string) => {
    toggle(value);
    syncExcluded();
  }, [toggle, syncExcluded]);

  const decoratedToggleAll = useCallback(() => {
    toggleAll();
    syncExcluded();
  }, [toggleAll, syncExcluded]);

  return { isAbsolute, onToggleAbsolute, toggle: decoratedToggle, toggleAll: decoratedToggleAll };
};

export default useAbsoluteFilter;
